import { useEffect, useMemo, useState } from 'react';
import { formatDate, type DocumentRequestChannel } from '@brimo/shared';
import { Banner, Select } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { completeTask, requestDocuments, useContactDocuments, useDocumentTypes } from '../api';
import { addDays, shortDay, todayKey } from '../format';
import type { DocumentRequestBody, DocumentRow, DocumentType } from '../types';
import { EditorCard, PrimaryAction } from './EditorCard';
import { CheckList, ChipGroup, EditorField } from './fields';
import { isNextStepRequired, isValidation, referencesOf, useNextStepGate, type EditorProps } from './shared';
import styles from './editor.module.css';

interface Item {
  id: string;
  title: string;
  documentTypeId: string | null;
  meta: string;
  open: boolean;
  checked: boolean;
}

const CHANNELS: ReadonlyArray<{ value: DocumentRequestChannel; label: string }> = [
  { value: 'email', label: 'E-Mail' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'portal', label: 'nur Portal' },
];

type Deadline = '3d' | '1w' | 'none';
const DEADLINES: ReadonlyArray<{ value: Deadline; label: string }> = [
  { value: '3d', label: '3 Tage' },
  { value: '1w', label: '1 Woche' },
  { value: 'none', label: 'ohne Frist' },
];

/** The checklist rows of the record: one per document type (or untyped name) with the status line of the screen. */
/** The customer's standard proofs (identity, proof of income) form the checklist even before anything was requested. */
const CHECKLIST_CATEGORIES: ReadonlySet<string> = new Set(['identity', 'proof_of_income']);

function itemsOf(rows: DocumentRow[], types: DocumentType[]): Item[] {
  const byKey = new Map<string, Item>();
  for (const type of types) {
    if (!CHECKLIST_CATEGORIES.has(type.category)) continue;
    byKey.set(`type:${type.id}`, {
      id: `type:${type.id}`,
      title: type.name,
      documentTypeId: type.id,
      meta: 'noch nicht angefordert',
      open: true,
      checked: false,
    });
  }
  for (const row of rows) {
    const key = row.documentType ? `type:${row.documentType.id}` : `name:${row.name}`;
    const existing = byKey.get(key);
    const open = row.kind === 'request' || row.status === 'missing' || row.status === 'requested';
    const meta =
      row.kind === 'request'
        ? `fehlt · angefordert ${shortDay(row.request?.requestedAt ?? row.uploadedAt)}`
        : row.status === 'reviewed'
          ? `liegt vor · geprüft ${shortDay(row.uploadedAt)}`
          : row.status === 'missing'
            ? 'fehlt'
            : `liegt vor · ${row.statusLabel} ${shortDay(row.uploadedAt)}`;
    if (existing && existing.meta === 'noch nicht angefordert') {
      existing.open = open;
      existing.checked = open;
      existing.meta = meta;
    } else if (existing && !existing.open && open) {
      existing.open = true;
      existing.checked = true;
      existing.meta = meta;
    } else if (!existing) {
      byKey.set(key, {
        id: key,
        title: row.documentType?.name ?? row.name,
        documentTypeId: row.documentType?.id ?? null,
        meta,
        open,
        checked: open,
      });
    }
  }
  return [...byKey.values()];
}

export interface DocumentRequestEditorProps extends EditorProps {
  /** Preselected channel („nur Portal" when opened from the record's portal documents, Mandantenportal 29). */
  initialChannel?: DocumentRequestChannel | undefined;
}

/** Kontaktakte II.E6 „Dokumente anfordern": checklist, channel, text, deadline → `POST /document-requests`. */
export function DocumentRequestEditor({
  record,
  task,
  onClose,
  onDone,
  initialChannel = 'email',
}: DocumentRequestEditorProps) {
  const documents = useContactDocuments(record.id, true);
  const types = useDocumentTypes(true);
  const references = useMemo(() => referencesOf(record), [record]);
  const [items, setItems] = useState<Item[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [typeToAdd, setTypeToAdd] = useState('');
  const [custom, setCustom] = useState('');
  const [channel, setChannel] = useState<DocumentRequestChannel>(initialChannel);
  const [message, setMessage] = useState('');
  const [deadline, setDeadline] = useState<Deadline>('1w');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (seeded || !documents.data || !types.data) return;
    setItems(itemsOf(documents.data.items, types.data.items));
    setSeeded(true);
  }, [documents.data, seeded, types.data]);

  const selected = items.filter((item) => item.checked);
  const openCount = items.filter((item) => item.open).length;
  const dealReference = references.find((item) => item.dealId);
  const listLabel = dealReference ? `Checkliste ${dealReference.label}` : 'Checkliste';
  const typeOptions = (types.data?.items ?? [])
    .filter((type) => !items.some((item) => item.documentTypeId === type.id))
    .map((type) => ({ value: type.id, label: type.name }));

  function addType(id: string) {
    const type = types.data?.items.find((item) => item.id === id);
    if (!type) return;
    setItems((current) => [
      ...current,
      {
        id: `type:${type.id}`,
        title: type.name,
        documentTypeId: type.id,
        meta: 'noch nicht angefordert',
        open: true,
        checked: true,
      },
    ]);
    setTypeToAdd('');
  }

  function addCustom() {
    const title = custom.trim();
    if (!title) return;
    setItems((current) => [
      ...current,
      {
        id: `custom:${title}:${current.length}`,
        title,
        documentTypeId: null,
        meta: 'freie Angabe',
        open: true,
        checked: true,
      },
    ]);
    setCustom('');
  }

  const gate = useNextStepGate(record.header.name, async (step) => {
    if (!task) return;
    const done = await completeTask(task.id, { outcomeType: 'documents_requested', nextStep: step });
    onDone({
      toast: toastText(selected.length),
      next:
        done.next === 'book_appointment'
          ? { editor: 'appointment', completesTaskId: done.createdTaskId }
          : undefined,
    });
  });

  function toastText(count: number): string {
    return count === 1 ? '1 Dokument angefordert.' : `${count} Dokumente angefordert.`;
  }

  async function submit() {
    if (selected.length === 0) {
      setError('Bitte mindestens eine Unterlage anhaken.');
      return;
    }
    const body: DocumentRequestBody = {
      contactId: record.id,
      items: selected.map((item) =>
        item.documentTypeId
          ? { documentTypeId: item.documentTypeId, title: item.title }
          : { title: item.title },
      ),
      channel,
    };
    if (dealReference?.dealId) body.dealId = dealReference.dealId;
    if (deadline !== 'none') body.neededByOn = addDays(todayKey(), deadline === '3d' ? 3 : 7);
    if (message.trim()) body.message = message.trim();
    setPending(true);
    setError(null);
    try {
      await requestDocuments(body);
    } catch (cause) {
      setError(isValidation(cause) ? (Object.values(cause.fields)[0] ?? cause.message) : errorMessage(cause));
      setPending(false);
      return;
    }
    if (task) {
      try {
        const done = await completeTask(task.id, { outcomeType: 'documents_requested' });
        onDone({
          toast: toastText(selected.length),
          next:
            done.next === 'book_appointment'
              ? { editor: 'appointment', completesTaskId: done.createdTaskId }
              : undefined,
        });
      } catch (cause) {
        setPending(false);
        if (isNextStepRequired(cause)) gate.open();
        else setError(errorMessage(cause));
      }
      return;
    }
    onDone({ toast: toastText(selected.length) });
  }

  const count = selected.length;
  const dueText =
    deadline === 'none' ? null : `Frist ${formatDate(addDays(todayKey(), deadline === '3d' ? 3 : 7))}`;

  return (
    <>
      <EditorCard
        tone="paper"
        dot="var(--warning-fg)"
        title="Dokumente anfordern"
        subtitle="erzeugt eine Anforderung im Kundenportal"
        footnote="Die Anforderung erscheint im Portal des Kunden und als Aufgabe beim betreuenden Berater."
        label="Dokumente-Editor"
        onClose={onClose}
        primary={
          <PrimaryAction loading={pending} disabled={count === 0} onClick={() => void submit()}>
            {count === 1 ? '1 Dokument anfordern' : `${count} Dokumente anfordern`}
          </PrimaryAction>
        }
      >
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <EditorField
          label={listLabel}
          hint={items.length > 0 ? `${openCount} von ${items.length} offen` : undefined}
        >
          {items.length > 0 ? (
            <CheckList
              items={items.map((item) => ({
                id: item.id,
                label: item.title,
                meta: item.meta,
                checked: item.checked,
                onChange: (checked) =>
                  setItems((current) =>
                    current.map((row) => (row.id === item.id ? { ...row, checked } : row)),
                  ),
              }))}
            />
          ) : (
            <p className={styles.note} style={{ margin: 0 }}>
              {documents.isPending || types.isPending
                ? 'Unterlagen werden geladen …'
                : 'Noch keine Unterlagen in der Akte — wählen Sie unten, was angefordert wird.'}
            </p>
          )}
          <div className={styles.dateRow} style={{ marginTop: 8 }}>
            {typeOptions.length > 0 ? (
              <Select
                label="Unterlage hinzufügen"
                labelHidden
                size="compact"
                placeholder="Unterlage hinzufügen"
                options={typeOptions}
                value={typeToAdd}
                onChange={(event) => addType(event.target.value)}
              />
            ) : null}
            <input
              className={styles.input}
              style={{ width: 'auto', flex: 1, minWidth: 160 }}
              aria-label="Weitere Unterlage"
              placeholder="Weitere Unterlage (freier Titel)"
              value={custom}
              onChange={(event) => setCustom(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addCustom();
                }
              }}
              onBlur={addCustom}
            />
          </div>
        </EditorField>
        <EditorField label="Erinnern per">
          <ChipGroup label="Erinnern per" options={CHANNELS} value={channel} onChange={setChannel} />
        </EditorField>
        <EditorField label="Begleittext" hint="optional">
          <textarea
            className={styles.textarea}
            rows={2}
            aria-label="Begleittext"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
        </EditorField>
        <EditorField label="Frist" hint={dueText ?? undefined}>
          <ChipGroup label="Frist" options={DEADLINES} value={deadline} onChange={setDeadline} />
        </EditorField>
      </EditorCard>
      {gate.dialog}
    </>
  );
}
